import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { userStorageKey } from "@medoma/shared-employee/userStorageKey";
import { useUserProfile } from "@/api/Users";

export type InitialLeadingPanelSize = `${number}%` | `${number}px` | number;
type PanelSize = number;

// Parse the initial leading panel size, either a percentage or a pixel value,
// into a number that can be used for calculations. The result is always at least
// minPanelSize.
export const parseInitialLeadingPanelSize = (
  size: InitialLeadingPanelSize,
  containerSize: number | undefined,
  minPanelSize: PanelSize,
): PanelSize => {
  let result: number;

  if (typeof size === "number") {
    result = size;
  } else if (size.endsWith("%")) {
    if (containerSize === undefined) {
      return minPanelSize;
    }
    result = (parseFloat(size.replace("%", "")) / 100) * containerSize;
  } else {
    result = parseFloat(size);
  }

  return Math.round(Math.max(result, minPanelSize));
};

const KEYBOARD_STEP = 16;

export const clampPanelSize = (
  size: number,
  containerSize: number,
  minPanelSize: number,
) =>
  Math.round(
    Math.max(minPanelSize, Math.min(containerSize - minPanelSize, size)),
  );

// Drives a resizable two-panel layout by writing a `--leading-width` CSS custom
// property on the container. Inspired by the Planning view's useResizablePanels,
// but tailored to SplitPane: a single CSS variable (so the absolute-positioned
// panels are never touched), keyboard support, and opt-in localStorage persistence.
const useSplitPaneResize = ({
  initialLeadingPanelSize = "50%",
  minPanelSize = 300,
  panelSizeLocalStorageKey,
}: {
  initialLeadingPanelSize?: InitialLeadingPanelSize;
  minPanelSize?: number;
  panelSizeLocalStorageKey?: string;
}) => {
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);
  const currentSizeRef = useRef<PanelSize>(minPanelSize);
  const initializedRef = useRef(false);

  const [leadingPanelSize, setLeadingPanelSize] = useState<PanelSize>();
  const [containerWidth, setContainerWidth] = useState<number>();

  const { userId } = useUserProfile();

  const storageKey =
    panelSizeLocalStorageKey === undefined
      ? undefined
      : userStorageKey({
          userId,
          key: `split-pane-leading-width-${panelSizeLocalStorageKey}`,
        });

  const persist = useCallback(
    (size: number) => {
      if (storageKey !== undefined) {
        localStorage.setItem(storageKey, size.toString());
      }
    },
    [storageKey],
  );

  // Measure the container and resolve the initial leading size once it mounts
  // (a callback ref keeps the measurement out of an effect, avoiding a flash).
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerNodeRef.current = node;
      if (node === null || initializedRef.current) {
        return;
      }

      initializedRef.current = true;
      const width = node.getBoundingClientRect().width;
      const storedValue =
        storageKey === undefined ? null : localStorage.getItem(storageKey);
      const storedSize = storedValue === null ? NaN : Number(storedValue);
      // Use the stored size when it's a valid number, else the configured
      // initial. Clamp to the container so a size saved on a wider screen can't
      // push the detail panel off-view (width 0 = not laid out yet, clamp later).
      const resolvedSize = Number.isFinite(storedSize)
        ? storedSize
        : parseInitialLeadingPanelSize(
            initialLeadingPanelSize,
            width,
            minPanelSize,
          );
      const size =
        width > 0
          ? clampPanelSize(resolvedSize, width, minPanelSize)
          : resolvedSize;

      currentSizeRef.current = size;
      setLeadingPanelSize(size);
      setContainerWidth(width);
    },
    [storageKey, initialLeadingPanelSize, minPanelSize],
  );

  // Reflect the size onto the container as a CSS custom property.
  useLayoutEffect(() => {
    if (leadingPanelSize !== undefined) {
      containerNodeRef.current?.style.setProperty(
        "--leading-width",
        `${leadingPanelSize}px`,
      );
    }
  }, [leadingPanelSize]);

  // Keep the container width in sync so the divider's aria values stay accurate.
  useEffect(() => {
    const container = containerNodeRef.current;
    if (container === null) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) {
        return;
      }

      const width = entry.contentRect.width;
      // Ignore hidden/detached states.
      if (width === 0) {
        return;
      }

      setContainerWidth(width);

      // Re-clamp the current size to the new bounds so the split layout and aria
      // values stay valid when the container shrinks.
      const clamped = clampPanelSize(
        currentSizeRef.current,
        width,
        minPanelSize,
      );
      if (clamped !== currentSizeRef.current) {
        currentSizeRef.current = clamped;
        setLeadingPanelSize(clamped);
      }
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [minPanelSize]);

  // Pointer + keyboard resizing are wired up imperatively so the rendered
  // separator stays a plain element (no tabIndex / onKeyDown attributes, which
  // jsx-a11y flags on a non-interactive element).
  useEffect(() => {
    const handle = handleRef.current;
    if (handle === null) {
      return;
    }

    const resizeTo = (size: number) => {
      currentSizeRef.current = size;
      setLeadingPanelSize(size);
    };

    const handleMouseMove = (event: MouseEvent) => {
      const container = containerNodeRef.current;
      if (!isResizingRef.current || container === null) {
        return;
      }

      const rect = container.getBoundingClientRect();
      resizeTo(
        clampPanelSize(event.clientX - rect.left, rect.width, minPanelSize),
      );
    };

    const stopResizing = () => {
      isResizingRef.current = false;
      persist(currentSizeRef.current);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
    };

    const startResizing = (event: MouseEvent) => {
      event.preventDefault(); // Prevents text selection while dragging.
      isResizingRef.current = true;
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResizing);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const container = containerNodeRef.current;
      if (container === null) {
        return;
      }

      const rect = container.getBoundingClientRect();
      const current = currentSizeRef.current;
      let next: number;

      switch (event.key) {
        case "ArrowLeft":
          next = current - KEYBOARD_STEP;
          break;
        case "ArrowRight":
          next = current + KEYBOARD_STEP;
          break;
        case "Home":
          next = minPanelSize;
          break;
        case "End":
          next = rect.width - minPanelSize;
          break;
        default:
          return;
      }

      event.preventDefault();
      resizeTo(clampPanelSize(next, rect.width, minPanelSize));
      persist(currentSizeRef.current);
    };

    handle.tabIndex = 0;
    handle.addEventListener("mousedown", startResizing);
    handle.addEventListener("keydown", handleKeyDown);

    return () => {
      handle.removeEventListener("mousedown", startResizing);
      handle.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResizing);
    };
  }, [minPanelSize, persist]);

  const toPercent = (value: number) =>
    containerWidth === undefined || containerWidth === 0
      ? undefined
      : Math.round((value / containerWidth) * 100);

  return {
    containerRef,
    handleRef,
    ariaValueNow:
      leadingPanelSize === undefined ? undefined : toPercent(leadingPanelSize),
    ariaValueMin: toPercent(minPanelSize),
    ariaValueMax:
      containerWidth === undefined
        ? undefined
        : toPercent(containerWidth - minPanelSize),
  };
};

export default useSplitPaneResize;
